<script setup lang="ts">
  import { ref, watchEffect, computed } from 'vue'
  import { calculateAutoLayoutColumnsForWidth } from '../../utils/layout/calculateAutoLayoutColumnsForWidth'
  import { useRedirectActions } from '../../composables/useRedirectActions'
  import { useNewsArticleHeadlines } from '../../composables/data-fetching/useNewsArticles'
  import type { NewsArticle } from '../../server/models/news/news-article'
  import { useWindowSize } from '@vueuse/core'
  import Outbrain from '../../components/AdOps/Outbrain.vue'

  const { sendToNewsPage } = useRedirectActions()
  const isLoading = ref(true)
  const articles = ref<NewsArticle[]>([])
  const { selectedCategory } = useSearchParams()
  const emit = defineEmits<{ 'used-articles': [count: number] }>()
  const { headlines } = useNewsArticleHeadlines(selectedCategory)
  const { width } = useWindowSize()
  const contentDisplaySettingsStore = useContentDisplaySettingsStore()
  const { featureFlags } = storeToRefs(contentDisplaySettingsStore)

  const billboardAdProps = computed(() => {
    const size =
      featureFlags.value.aboveFoldSectionAds?.billboardAdSize || 'large'
    const classes =
      featureFlags.value.aboveFoldSectionAds?.billboardAdClasses?.classes || ''
    const widgetId =
      featureFlags.value.aboveFoldSectionAds?.billboardAdClasses?.widgetId ||
      'AR_20'

    return {
      adHeight: 600,
      adWidth: size === 'small' ? 120 : size === 'medium' ? 160 : 300,
      widgetId,
      classes,
    }
  })

  const columns = computed(() => {
    const columnForWidth = calculateAutoLayoutColumnsForWidth(width.value)
    // AutoLayout columns sizes minus the space for the 2x2 tile and the ad tile
    return columnForWidth - 3
  })

  const displayedArticles = computed(() => {
    const start = 1 // Skip first article (used in 2x2 tile)
    const maxArticlesNeeded = columns.value * 2 // Adjust for grid layout
    return headlines.value?.slice(start, start + maxArticlesNeeded) || []
  })

  const gridClass = computed(() => {
    if (columns.value < 0) return ''
    const baseClasses = 'grid row-span-2 gap-12px'
    const colClasses: { [key: number]: string } = {
      0: 'grid-cols-1',
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
    }
    return `${baseClasses} ${colClasses[columns.value]}`
  })

  const getArticleProps = (article: NewsArticle) => ({
    id: article.id,
    headline: article.title,
    subtitle: article.subtitle,
    'thumbnail-url': article.thumbnailUrl ?? '',
    'publisher-name': article.provider,
    'published-date': article.publishedDate,
    'publisher-icon-url': article.providerLogoUrl,
    'article-url': article.link,
    design: 'msn' as const,
    role: 'article',
  })

  watchEffect(
    () => {
      if (!headlines.value?.length) return

      articles.value = headlines.value.slice(0, 7)
      isLoading.value = false

      const totalSlots = columns.value * 2
      emit('used-articles', 1 + totalSlots)
    },
    { flush: 'post' }
  )
</script>

<template v-if="!isLoading">
  <div
    class="flex items-center gap-12px justify-center py-8px"
    :class="[columns < 0 ? 'flex-col' : 'flex-row']"
  >
    <!-- 2x2 Tile -->
    <div class="row-span-2">
      <ArticleCard
        v-if="articles[0]"
        v-bind="getArticleProps(articles[0])"
        variant="2x2-top-headline"
        @click="sendToNewsPage"
      />
    </div>

    <!-- Rows of regular article cards -->
    <div v-if="columns > 0" :class="gridClass">
      <template v-for="index in columns * 2" :key="index">
        <ArticleCard
          v-if="displayedArticles[index - 1]"
          v-bind="getArticleProps(displayedArticles[index - 1])"
          variant="default"
          @click="sendToNewsPage"
        />
      </template>
    </div>
    <div
      class="h-[600px] w-[300px] row-span-2 flex items-center justify-center"
    >
      <Outbrain
        :placement="billboardAdProps.classes"
        :widget-id="billboardAdProps.widgetId"
        :ad-height="billboardAdProps.adHeight"
        :ad-width="billboardAdProps.adWidth"
      />
    </div>
  </div>
</template>
